import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/shared/i18n";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Plus, Megaphone, Mail, Loader2 } from "lucide-react";
import { useCampaigns } from "../model/hooks";
import type { Campaign, CampaignStatus, CampaignChannel } from "../types";

function StatusBadge({ status }: { status: CampaignStatus }) {
  const { t } = useTranslation("bulk-notifications");
  const key =
    status === "draft"
      ? "statusDraft"
      : status === "scheduled"
        ? "statusScheduled"
        : status === "sent"
          ? "statusSent"
          : "statusCancelled";
  const variant =
    status === "sent"
      ? "default"
      : status === "scheduled"
        ? "secondary"
        : status === "draft"
          ? "outline"
          : "destructive";
  return (
    <Badge variant={variant} className="cf-badge-pill rounded-full">
      {t(key)}
    </Badge>
  );
}

function ChannelIcon({ channel }: { channel: CampaignChannel }) {
  return channel === "email" ? (
    <Mail className="h-4 w-4 text-muted-foreground" />
  ) : (
    <Megaphone className="h-4 w-4 text-muted-foreground" />
  );
}

export function CampaignsListPage() {
  const { t } = useTranslation("bulk-notifications");
  const navigate = useNavigate();
  const { campaigns, isLoading, error } = useCampaigns();

  if (isLoading) {
    return (
      <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
        <div>
          <h1 className="cf-page-title">{t("title")}</h1>
          <p className="cf-page-subtitle">{t("campaignsList")}</p>
        </div>
        <div className="cf-card">
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--cf-text-muted)] mb-4" aria-hidden />
            <p className="text-sm text-[var(--cf-text-muted)]">{t("loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
        <div>
          <h1 className="cf-page-title">{t("title")}</h1>
        </div>
        <div className="cf-card">
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <p className="text-[var(--cf-danger)]">{t("errorLoad")}</p>
            <Button
              variant="outline"
              className="border-[var(--cf-border)] text-[var(--cf-text)]"
              onClick={() => window.location.reload()}
            >
              {t("back")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-financial-page -m-6 space-y-8 p-8 pb-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="cf-page-title">{t("title")}</h1>
          <p className="cf-page-subtitle">
            {campaigns.length === 0
              ? t("emptyCampaigns")
              : `${campaigns.length} campanha(s)`}
          </p>
        </div>
        <Button
          className="cf-btn-primary shrink-0"
          onClick={() => navigate("/notifications/campaigns/new")}
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden />
          {t("newCampaign")}
        </Button>
      </div>

      <div className="cf-card">
        {campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <Megaphone className="h-12 w-12 text-[var(--cf-text-muted)] mb-4" aria-hidden />
            <p className="text-[var(--cf-text)] font-medium">{t("emptyCampaigns")}</p>
            <Button
              className="cf-btn-primary mt-2"
              onClick={() => navigate("/notifications/campaigns/new")}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              {t("newCampaign")}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                <TableHead className="text-[var(--cf-text-muted)]">{t("campaignTitle")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("channel")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("status")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("scheduledAt")}</TableHead>
                <TableHead className="text-[var(--cf-text-muted)]">{t("recipientCount")}</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  className="cursor-pointer border-[var(--cf-border)] hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                  onClick={() =>
                    navigate(`/notifications/campaigns/${campaign.id}`)
                  }
                >
                  <TableCell className="font-medium text-[var(--cf-text)]">{campaign.title}</TableCell>
                  <TableCell className="text-[var(--cf-text)]">
                    <span className="flex items-center gap-2">
                      <ChannelIcon channel={campaign.channel} />
                      {campaign.channel === "email"
                        ? t("channelEmail")
                        : t("channelNotification")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={campaign.status} />
                  </TableCell>
                  <TableCell className="text-[var(--cf-text-muted)] text-sm">
                    {campaign.scheduledAt
                      ? format(
                          new Date(campaign.scheduledAt),
                          "dd/MM/yyyy HH:mm",
                          { locale: ptBR }
                        )
                      : "—"}
                  </TableCell>
                  <TableCell className="text-[var(--cf-text)]">{campaign.recipientCount}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-[var(--cf-primary)] hover:text-[var(--cf-primary)] hover:bg-[var(--cf-warning-bg)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/notifications/campaigns/${campaign.id}/dispatches`
                        );
                      }}
                    >
                      {t("dispatches")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
